// @ts-nocheck
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronLeft, Plus, Home, FileText, Users, Package, CreditCard, LogOut, Settings, Menu, X, Cloud,
  LayoutDashboard, Globe, Shield, Activity, UserCircle, Sliders, Crown, SlidersHorizontal, Image, Phone, Instagram, MessageSquare, Languages
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { SiteSwitcher } from '../components/SiteSwitcher';
import { ModuleGuard } from '../components/ModuleGuard';
import { useModuleNavigation } from '../hooks/useModuleNavigation';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import { DocumentHead } from '../components/DocumentHead';

const cls = (isActive: boolean) =>
  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
    isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
  }`;

const subCls = (isActive: boolean) =>
  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
    isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-100'
  }`;

export function AppLayout() {
  const { user, signOut, enabledModules, currentSite, isSuperAdmin, avatarUrl, fullName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [settingsOpen, setSettingsOpen] = useState(location.pathname === '/settings');
  const [adminOpen, setAdminOpen] = useState(location.pathname.startsWith('/admin'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (location.pathname === '/settings') setSettingsOpen(true);
    if (location.pathname.startsWith('/admin')) setAdminOpen(true);
    setSidebarOpen(false);
  }, [location.pathname]);
  const platformSettings = usePlatformSettings();

  const displayLogo = platformSettings.platform_logo_url;
  const displayName = platformSettings.platform_name || 'AFCMS';

  const handleLogout = async () => { await signOut(); navigate('/login'); };

  const hassite = Boolean(currentSite);

  function toggleGroup(groupId: string) {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }

  // Use ModuleRegistry-based navigation
  const navGroups = useModuleNavigation(enabledModules, isSuperAdmin);

  function isGroupActive(group: { items: Array<{ to: string }> }): boolean {
    return group.items.some(item => location.pathname === item.to || location.pathname.startsWith(item.to + '/'));
  }

  return (
    <div className="min-h-screen flex flex-col">
      <DocumentHead />
      {/* Top Header */}
      <header className="h-14 bg-white border-b border-gray-200 flex items-center px-4 justify-between z-30 flex-shrink-0">
        <button
          className="md:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600 ml-1"
          onClick={() => setSidebarOpen(o => !o)}
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <div className="flex items-center gap-3">
          {displayLogo ? (
            <img src={displayLogo} alt="Logo" className="h-8 w-auto object-contain" />
          ) : (
            <span className="text-lg font-bold text-primary-600">{displayName}</span>
          )}
          {currentSite && (
            <span className="text-sm text-gray-400 hidden sm:block">· {currentSite.name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => setProfileOpen(o => !o)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-bold text-primary-600">
                    {fullName ? fullName[0].toUpperCase() : (user?.email?.[0]?.toUpperCase() || 'A')}
                  </span>
                )}
              </div>
              <span className="text-sm font-medium hidden sm:block truncate max-w-[150px]">
                {fullName || user?.email || 'کاربر'}
              </span>
              <ChevronDown size={14} className="text-gray-500" />
            </button>

            {profileOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setProfileOpen(false)} />
                <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium truncate">{fullName || user?.email || 'کاربر'}</p>
                    <p className="text-xs text-gray-500">{isSuperAdmin ? 'سوپر ادمین' : 'عضو'}</p>
                  </div>
                  <NavLink to="/profile" onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <UserCircle size={16} />پروفایل
                  </NavLink>
                  <NavLink to="/settings" onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <Settings size={16} />تنظیمات
                  </NavLink>
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                      <LogOut size={16} />خروج
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-20 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed md:static inset-y-0 right-0 z-30
          w-64 md:w-60 bg-white border-l border-gray-200 flex flex-col flex-shrink-0
          transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
          top-14 md:top-0
        `}>
          <div className="p-3 border-b border-gray-200">
            <SiteSwitcher />
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {/* Always visible items */}
            <NavLink to="/" end className={({ isActive }) => cls(isActive)}>
              <LayoutDashboard size={18} /><span>داشبورد</span>
            </NavLink>

            {/* Super admin menu - always at top */}
            {isSuperAdmin && (
              <>
                <button
                  onClick={() => setAdminOpen(o => !o)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    location.pathname.startsWith('/admin') ? 'bg-purple-50 text-purple-700' : 'text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <Shield size={18} />
                  <span className="flex-1 text-right">پنل سوپر ادمین</span>
                  <ChevronLeft size={14} className={`transition-transform ${adminOpen ? '-rotate-90' : ''}`} />
                </button>
                {adminOpen && (
                  <div className="mr-4 space-y-0.5 mt-0.5">
                    {[
                      { to: '/admin',                label: 'داشبورد',           Icon: LayoutDashboard, end: true },
                      { to: '/admin/users',          label: 'کاربران',           Icon: UserCircle },
                      { to: '/admin/sites',          label: 'سایت‌ها',           Icon: Globe },
                      { to: '/admin/plans',          label: 'پلن‌ها',            Icon: Crown },
                      { to: '/admin/module-limits',  label: 'محدودیت ماژول‌ها', Icon: Sliders },
                      { to: '/admin/system-settings',   label: 'تنظیمات سیستم',    Icon: SlidersHorizontal },
                      { to: '/admin/platform-storage',  label: 'ذخیره‌سازی پلتفرم', Icon: Cloud },
                    ].map(({ to, label, Icon, end }) => {
                      const isActive = end
                        ? location.pathname === to
                        : location.pathname.startsWith(to);
                      return (
                        <NavLink
                          key={to}
                          to={to}
                          end={!!end}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            isActive ? 'bg-purple-50 text-purple-700 font-medium' : 'text-purple-500 hover:bg-purple-50'
                          }`}
                        >
                          <Icon size={14} />
                          <span>{label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
                <div className="pt-1 mt-1 border-t border-gray-100" />
              </>
            )}

            {!hassite && (
              <p className="text-xs text-gray-400 px-3 py-2 mt-1">ابتدا یک سایت انتخاب کنید</p>
            )}

            {/* Core pages link - always visible when site selected */}
            {hassite && (
              <>
                <NavLink to="/pages" className={({ isActive }) => cls(isActive)}>
                  <FileText size={18} /><span>صفحات</span>
                </NavLink>
                <NavLink to={`/sites/${currentSite.id}/users`} className={({ isActive }) => cls(isActive)}>
                  <Users size={18} /><span>کاربران سایت</span>
                </NavLink>

                {/* Shop Section */}
                {(() => {
                  const isShopExpanded = expandedGroups.has('shop');
                  const isShopActive = location.pathname.includes('/shop');
                  return (
                    <div>
                      <button
                        onClick={() => toggleGroup('shop')}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isShopActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Package size={18} />
                        <span className="flex-1 text-right">فروشگاه</span>
                        <ChevronDown size={16} className={`transition-transform ${isShopExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      {isShopExpanded && (
                        <div className="mr-4 mt-1 space-y-0.5">
                          <NavLink to="/shop" className={({ isActive }) => subCls(isActive)}>
                            <LayoutDashboard size={16} /><span>محصولات</span>
                          </NavLink>
                          <NavLink to="/shop/orders" className={({ isActive }) => subCls(isActive)}>
                            <Package size={16} /><span>سفارشات</span>
                          </NavLink>
                          <NavLink to="/shop/categories" className={({ isActive }) => subCls(isActive)}>
                            <LayoutDashboard size={16} /><span>دسته‌بندی‌ها</span>
                          </NavLink>
                          <NavLink to="/shop/settings" className={({ isActive }) => subCls(isActive)}>
                            <Settings size={16} /><span>تنظیمات فروشگاه</span>
                          </NavLink>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}

            {/* Module groups from ModuleRegistry */}
            {navGroups
              .filter((group: { id: string }) => group.id !== 'shop')
              .map((group: { id: string; label: string; icon: React.ComponentType<{ size?: number }>; items: Array<{ to: string; label: string; icon: React.ComponentType<{ size?: number }> | null; permission?: string }> }) => {
              const isExpanded = expandedGroups.has(group.id);
              const isActive = isGroupActive(group);
              const GroupIcon = group.icon;

              return (
                <div key={group.id}>
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <GroupIcon size={18} />
                    <span className="flex-1 text-right">{group.label}</span>
                    <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="mr-4 mt-1 space-y-0.5">
                      {group.items.map((item: { to: string; label: string; icon: React.ComponentType<{ size?: number }> | null }) => {
                        const ItemIcon = item.icon;
                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            end
                            className={({ isActive }) => subCls(isActive)}
                          >
                            {ItemIcon && <ItemIcon size={14} />}
                            <span>{item.label}</span>
                          </NavLink>
                        );
                      })}
                      {group.id === 'blog' && (
                        <NavLink to="/blog/comments" className={({ isActive }) => subCls(isActive)}>
                          <MessageSquare size={14} /><span>نظرات</span>
                        </NavLink>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Site Settings group */}
            {hassite && (
              <>
                <div className="pt-1 mt-1 border-t border-gray-100" />
                <button
                  onClick={() => setSettingsOpen(o => !o)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    location.pathname === '/settings' ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Settings size={18} />
                  <span className="flex-1 text-right">تنظیمات وبسایت</span>
                  <ChevronLeft size={14} className={`transition-transform ${settingsOpen ? '-rotate-90' : ''}`} />
                </button>
                {settingsOpen && (
                  <div className="mr-4 space-y-0.5 mt-0.5">
                    {[
                      { tab: 'general',    label: 'تنظیمات پایه',     Icon: Globe },
                      { tab: 'branding',   label: 'لوگو و برند',      Icon: Image },
                      { tab: 'contact',    label: 'اطلاعات تماس',     Icon: Phone },
                      { tab: 'social',     label: 'شبکه‌های اجتماعی', Icon: Instagram },
                      { tab: 'navigation', label: 'مدیریت منوها',     Icon: Menu },
                    ].map(({ tab, label, Icon }) => {
                      const isActive = location.pathname === '/settings' && location.search === `?tab=${tab}`;
                      return (
                        <NavLink
                          key={tab}
                          to={`/settings?tab=${tab}`}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          <Icon size={14} />
                          <span>{label}</span>
                        </NavLink>
                      );
                    })}
                    <NavLink to="/roles-permissions" className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
                      <Shield size={14} />
                      <span>نقش‌ها و دسترسی‌ها</span>
                    </NavLink>
                    <NavLink to="/site-members" className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
                      <Users size={14} />
                      <span>اعضای سایت</span>
                    </NavLink>
                  </div>
                )}
              </>
            )}

            {/* Activity log */}
            <div className="pt-1 mt-1 border-t border-gray-100" />
            <NavLink to="/translations" className={({ isActive }) => cls(isActive)}>
              <ModuleGuard moduleId="translations">
                <Languages size={18} /><span>ترجمه‌ها</span>
              </ModuleGuard>
            </NavLink>
            <NavLink to="/audit-log" className={({ isActive }) => cls(isActive)}>
              <Activity size={18} /><span>فعالیت‌ها</span>
            </NavLink>
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
